import React, { ReactChild } from 'react';
import { css } from '@emotion/react';
import UserInitial from '../../../../components/UserInitialBox';
import { Box, Flex, theme, Button, Text } from '@xometry/xometry_loft';
import { Organization } from '../../../../schema/generated/schema';
import TeamOnboardingPanel from '../../../TeamOnboardingPanel';

const IconBox = ({ children }: { children: ReactChild | ReactChild[] }) => {
  return (
    <Flex
      width="24px"
      height="24px"
      alignItems="center"
      justifyContent="center"
      flexWrap="wrap"
    >
      {children}
    </Flex>
  );
};

interface SideBarHeaderProps {
  itemKey?: string;
  children?: ReactChild | ReactChild[];
}

export const SideBarHeader = ({ children }: SideBarHeaderProps) => {
  return (
    <Flex
      padding="8px 4px 0px 4px"
      color={theme.colors.darkBlues[100]}
      lineHeight={theme.lineHeights[1]}
      fontSize={theme.fontSizes[0]}
      fontWeight={theme.fontWeights.semibold}
      width="fill-available"
      height={theme.space[8]}
      gap={theme.space[3]}
      alignItems="center"
    >
      {children}
    </Flex>
  );
};

interface SideBarTabItemProps {
  itemKey: string;
  active: string;
  setActiveView?: (view: string) => void;
  tooltipText: string;
  iconName: string;
  optionName: string;
}

const SideBarTabItem = ({
  itemKey,
  active,
  setActiveView,
  tooltipText,
  iconName,
  optionName,
}: SideBarTabItemProps) => {
  let textColor = theme.colors.darkBlues[90];
  const isActiveView = active === itemKey;

  if (isActiveView) {
    textColor = theme.colors.blues[100];
  }
  // this is needed to able to select an active view that already is active to "refresh" the view
  const handleSelectActiveView = () => {
    if (setActiveView) {
      setActiveView('');
      setTimeout(() => setActiveView(itemKey), 0);
    }
  };
  return (
    <Box width="100%">
      <Button
        title={tooltipText}
        kind="transparent-dark"
        scale="medium"
        type="button"
        onClick={handleSelectActiveView}
        width="100%"
        padding={`0px ${theme.space[4]}`}
        backgroundColor={isActiveView ? theme.colors.white : ''}
        justifyContent="flex-start"
        css={css`
          &:focus:not(:active),
          &:hover {
            box-shadow: none;
            background-color: ${theme.colors.white};
          }
        `}
      >
        <Flex
          color={textColor}
          lineHeight={theme.lineHeights[2]}
          fontSize={theme.fontSizes[0]}
          height={theme.space[8]}
          gap={theme.space[3]}
          alignItems="center"
        >
          <IconBox>
            <i
              className={iconName}
              css={css`
                padding-left: 4px;
              `}
            />
          </IconBox>
          <Flex alignContent="center" flexWrap="wrap">
            <Text
              kind="body-title"
              css={css`
                color: ${textColor};
              `}
            >
              {optionName}
            </Text>
          </Flex>
        </Flex>
      </Button>
    </Box>
  );
};

const SideBar = ({
  org,
  active,
  setActiveView,
  onboardingPanelKey,
}: {
  active: string;
  org?: Organization;
  setActiveView: (view: string) => void;
  onboardingPanelKey: number;
}) => {
  return (
    <Flex
      alignItems="flex-start"
      justifyContent="flex-start"
      flexDirection="column"
      width="100%"
      css={css`
        gap: 12px;
      `}
    >
      {org?.name && (
        <>
          <SideBarHeader itemKey="header">
            <UserInitial fullName={org.name} role="org" size={30} />
            <Flex alignContent="center" flexWrap="wrap">
              <Text kind="panel-title" fontWeight={theme.fontWeights.semibold}>
                {' '}
                {org?.name}
              </Text>
            </Flex>
          </SideBarHeader>
          <SideBarTabItem
            itemKey="teamManagementView"
            active={active}
            setActiveView={setActiveView}
            tooltipText="You can manage or view who is on your team, what are their roles and what are the latest Team management activities that the Team Members performed."
            iconName="fa-solid fa-user-group fa-xl"
            optionName="Team Management"
          />
          <SideBarTabItem
            itemKey="sharingDefaultsView"
            active={active}
            setActiveView={setActiveView}
            tooltipText="Use these settings to enable Sharing your Quote and Order History with the Team."
            iconName="far fa-share-nodes fa-xl"
            optionName="Sharing"
          />
          <SideBarTabItem
            itemKey="notificationsView"
            active={active}
            setActiveView={setActiveView}
            tooltipText="You can select with Email Notifications you would like to receive regarding Quotes or Order updates within your team."
            iconName="far fa-bell fa-xl"
            optionName="Notifications"
          />
          <SideBarTabItem
            itemKey="teamShippingView"
            active={active}
            setActiveView={setActiveView}
            tooltipText="Here you can see the shipping settings for your team."
            iconName="far fa-truck fa-lg"
            optionName="Shipping"
          />
          <SideBarTabItem
            itemKey="quotePresetsView"
            active={active}
            setActiveView={setActiveView}
            tooltipText="View the Quote Presets for your team."
            iconName="far fa-sliders fa-lg"
            optionName="Quote Presets"
          />
          <Flex marginTop="58px" alignSelf="center">
            <TeamOnboardingPanel key={onboardingPanelKey} orgId={org.id} />
          </Flex>
        </>
      )}
      {/* Hide this for now, until Permission functionality is added */}
      {/* <SideBarItem itemKey="permissions" active={active}>
        <IconBox>
          <i className="fa-solid fa-clipboard-check fa-xl" />
        </IconBox>
        <Flex alignContent="center" flexWrap="wrap">
          Team Permissions
        </Flex>
      </SideBarItem> */}
    </Flex>
  );
};

export default SideBar;
